import React from 'react';
import styled from 'styled-components';
import type { AnsweringServicePayer } from '@/app-package';
import { AnsweringServiceType } from '@bb/domain/dist/types/AnsweringSubscription';
import OrgPlanCard, { PlanTier, OrgPlanPayer } from './OrgPlanCard';
import AnsweringServiceCard from './AnsweringServiceCard';

export interface CurrentServicesSectionProps {
  /** Whether there was an error fetching the Answering Service subscription data. */
  fetchDataError: boolean;
  /** The type of Org Plan this brokerage is on. Agent Plan (Tier 1) or Brokerage Plan (Tier 2). */
  currentOrgPlan: PlanTier;
  /** How the Org Plan subscription is billed */
  orgPlanPayer: OrgPlanPayer;
  /** Whether there is an already active Answering Service subscription for this user/brokerage */
  hasActiveAnsweringService: boolean;
  /** What type of Answering Service this user/brokerge has (or is eligible to sign up for) */
  answeringServiceType: AnsweringServiceType;
  /** Who pays for the Answering Service subscription */
  answeringServicePayer: AnsweringServicePayer;
  /** Callback when Org Plan View Details/Learn More is clicked */
  onOrgPlanClick: () => void;
  /** Callback when Live Concierge View Details is clicked */
  onAnsweringServiceClick: () => void;
}

/**
 * The "Current Services" section of the Subscription & Payments page
 * displaying all of the user/brokerage's active subscriptions.
 */
const CurrentServicesSection: React.FC<CurrentServicesSectionProps> = ({
  fetchDataError,
  currentOrgPlan,
  orgPlanPayer,
  hasActiveAnsweringService = false,
  answeringServiceType = AnsweringServiceType.LiveConciergePlus,
  answeringServicePayer,
  onOrgPlanClick,
  onAnsweringServiceClick,
}) => {
  const hasAnyCurrentServices = currentOrgPlan || hasActiveAnsweringService;

  // If the user has no subscriptions being paid for, don't display anything
  if (!hasAnyCurrentServices) {
    return null;
  }

  return (
    <Container>
      <Title>Current Services</Title>
      <CardsContainer>
        {
          <OrgPlanCard
            currentOrgPlan={currentOrgPlan}
            orgPlanPayer={orgPlanPayer}
            onButtonClick={onOrgPlanClick}
          />
        }
        {hasActiveAnsweringService &&
          answeringServiceType !== AnsweringServiceType.Legacy && (
            <AnsweringServiceCard
              fetchDataError={fetchDataError}
              isSubscribed
              answeringServiceType={answeringServiceType}
              answeringServicePayer={answeringServicePayer}
              onButtonClick={onAnsweringServiceClick}
            />
          )}
      </CardsContainer>
    </Container>
  );
};

export default CurrentServicesSection;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Title = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #2b2b2b;
  margin: 0;
`;

const CardsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;
